/**
 * Tiny dependency-free chiptune engine. A look-ahead scheduler (the standard
 * Web Audio gapless-loop pattern) drives a few oscillator "voices" from compact
 * note data. Shares the AudioContext + a dedicated music bus with audio.ts so
 * music has its own volume and bypasses the SFX reverb.
 *
 * Tracks are authored as note-name sequences ("A4/2" = A4 for 2 beats, "-/1" =
 * a one-beat rest). Mellow waveforms + gentle envelopes keep it soothing.
 */
import { audioContext, musicDestination } from './audio.js';
import { getPrefs } from './storage.js';

interface Note {
  p: number | null; // MIDI note, or null for a rest
  d: number; // duration in beats
}
interface Channel {
  wave: OscillatorType;
  gain: number; // relative level
  detune?: number;
  notes: Note[];
}
interface Track {
  bpm: number;
  channels: Channel[];
}

const SEMI: Record<string, number> = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };

function noteToMidi(s: string): number | null {
  if (s === '-' || s === '') return null;
  const m = /^([A-G][#b]?)(-?\d)$/.exec(s);
  if (!m) return null;
  return (parseInt(m[2], 10) + 1) * 12 + SEMI[m[1]];
}

/** Parse "A4/2 C5/1 -/1" into notes (default duration 1 beat). */
function seq(s: string): Note[] {
  return s
    .trim()
    .split(/\s+/)
    .map((tok) => {
      const [name, d] = tok.split('/');
      return { p: noteToMidi(name), d: d ? parseFloat(d) : 1 };
    });
}

function midiToFreq(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}

// ── Themes ──────────────────────────────────────────────────────
// Calm A-minor / C-major pentatonic loops (consonant by construction).
const LOBBY: Track = {
  bpm: 92,
  channels: [
    { wave: 'triangle', gain: 0.34, notes: seq('A4/2 C5/2 E5/2 D5/2 C5/2 A4/2 E4/2 G4/2 A4/2 C5/2 E5/2 G5/2 E5/2 D5/2 C5/2 -/2') },
    { wave: 'sine', gain: 0.3, detune: 0, notes: seq('A2/4 F2/4 C3/4 E2/4 A2/4 F2/4 G2/4 E2/4') },
    { wave: 'triangle', gain: 0.12, notes: seq('E4/4 C4/4 G4/4 B3/4 E4/4 C4/4 D4/4 B3/4') },
  ],
};

const HOLE_THEMES: Track[] = [
  {
    bpm: 116,
    channels: [
      { wave: 'square', gain: 0.18, notes: seq('A4/1 E5/1 C5/1 E5/1 D5/1 A4/1 C5/1 E5/1 G4/1 D5/1 B4/1 D5/1 C5/1 G4/1 E4/1 G4/1') },
      { wave: 'triangle', gain: 0.3, notes: seq('A2/2 A2/2 F2/2 F2/2 C3/2 C3/2 G2/2 E2/2') },
    ],
  },
  {
    bpm: 104,
    channels: [
      { wave: 'square', gain: 0.16, notes: seq('C5/1.5 E5/0.5 G5/1 E5/1 D5/1.5 C5/0.5 A4/1 C5/1 F4/1.5 A4/0.5 C5/1 A4/1 G4/2 -/2') },
      { wave: 'triangle', gain: 0.3, notes: seq('C3/2 C3/2 A2/2 A2/2 F2/2 G2/2 C3/2 G2/2') },
    ],
  },
  {
    bpm: 124,
    channels: [
      { wave: 'square', gain: 0.16, notes: seq('E5/1 D5/1 C5/1 D5/1 E5/1 E5/1 E5/2 D5/1 D5/1 D5/2 E5/1 G5/1 G5/2') },
      { wave: 'triangle', gain: 0.3, notes: seq('E2/2 E2/2 A2/2 A2/2 C3/2 C3/2 B2/2 B2/2') },
    ],
  },
];

// A more driving theme for the toughest/finale holes.
const FINALE: Track = {
  bpm: 132,
  channels: [
    { wave: 'square', gain: 0.16, notes: seq('A4/0.5 A4/0.5 E5/1 G5/0.5 F5/0.5 E5/1 D5/0.5 C5/0.5 E5/1 A5/1 G5/0.5 E5/0.5 D5/1 C5/0.5 A4/0.5 E4/1') },
    { wave: 'sawtooth', gain: 0.1, detune: 6, notes: seq('A4/1 E5/1 D5/1 C5/1 E5/1 A4/1 C5/1 E5/1') },
    { wave: 'triangle', gain: 0.32, notes: seq('A2/1 A2/1 F2/1 F2/1 D3/1 D3/1 E3/1 E2/1') },
  ],
};

function musicEnabled(): boolean {
  try {
    return getPrefs().music;
  } catch {
    return true;
  }
}
function musicVol(): number {
  try {
    return getPrefs().musicVolume;
  } catch {
    return 0.4;
  }
}

interface Voice {
  ch: Channel;
  idx: number;
  nextTime: number;
}

const LOOKAHEAD_S = 0.15; // schedule this far ahead
const TICK_MS = 40;

class MusicEngine {
  private trackGain: GainNode | null = null;
  private track: Track | null = null;
  private voices: Voice[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private anchored = false;
  private gestureBound = false;

  /** Re-resume the context as soon as the user interacts (autoplay policy). */
  private bindGesture(): void {
    if (this.gestureBound || typeof window === 'undefined') return;
    this.gestureBound = true;
    const kick = () => audioContext();
    window.addEventListener('pointerdown', kick, { passive: true });
    window.addEventListener('keydown', kick);
    window.addEventListener('touchstart', kick, { passive: true });
  }

  playLobby(): void {
    this.start(LOBBY);
  }

  /** Pick a hole theme by index; the last theme is reserved for finales. */
  playHole(holeIndex: number, holeCount: number): void {
    const isFinale = holeCount > 0 && holeIndex >= holeCount - 1 && holeCount >= 3;
    this.start(isFinale ? FINALE : HOLE_THEMES[holeIndex % HOLE_THEMES.length]);
  }

  private start(track: Track): void {
    this.bindGesture();
    if (!musicEnabled()) {
      this.stop();
      this.track = track; // remember intent; refreshSettings can resume it
      return;
    }
    const a = audioContext();
    const bus = musicDestination();
    if (!a || !bus) return;
    if (this.track === track && this.timer) return; // already playing it

    if (!this.trackGain) {
      this.trackGain = a.createGain();
      this.trackGain.connect(bus);
    }
    // Quick crossfade: duck, then swap voices and bring it back.
    const now = a.currentTime;
    this.trackGain.gain.cancelScheduledValues(now);
    this.trackGain.gain.setValueAtTime(this.trackGain.gain.value, now);
    this.trackGain.gain.linearRampToValueAtTime(0.0001, now + 0.25);

    this.track = track;
    this.anchored = false;
    setTimeout(() => {
      this.voices = track.channels.map((ch) => ({ ch, idx: 0, nextTime: 0 }));
      if (this.trackGain) {
        const t = (audioContext()?.currentTime ?? 0);
        this.trackGain.gain.cancelScheduledValues(t);
        this.trackGain.gain.setValueAtTime(0.0001, t);
        this.trackGain.gain.linearRampToValueAtTime(musicVol(), t + 0.4);
      }
    }, 260);

    if (!this.timer) this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private tick(): void {
    const a = audioContext();
    if (!a || !this.track || !this.trackGain) return;
    if (a.state !== 'running') {
      this.anchored = false; // re-sync once the context resumes after a gesture
      return;
    }
    if (!this.anchored) {
      const base = a.currentTime + 0.1;
      for (const v of this.voices) v.nextTime = base;
      this.anchored = true;
    }
    const beatSec = 60 / this.track.bpm;
    const horizon = a.currentTime + LOOKAHEAD_S;
    for (const v of this.voices) {
      let guard = 0;
      while (v.nextTime < horizon && guard++ < 64) {
        const note = v.ch.notes[v.idx];
        const dur = note.d * beatSec;
        if (note.p !== null) this.playNote(a, v.ch, note.p, v.nextTime, dur);
        v.nextTime += dur;
        v.idx = (v.idx + 1) % v.ch.notes.length;
      }
    }
  }

  private playNote(a: AudioContext, ch: Channel, midi: number, t: number, dur: number): void {
    const osc = a.createOscillator();
    const g = a.createGain();
    osc.type = ch.wave;
    if (ch.detune) osc.detune.value = ch.detune;
    osc.frequency.value = midiToFreq(midi);
    const peak = ch.gain;
    const stop = t + dur;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * 0.55), t + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, stop - Math.min(0.03, dur * 0.1));
    osc.connect(g);
    g.connect(this.trackGain as GainNode);
    osc.start(t);
    osc.stop(stop + 0.02);
  }

  /** Re-read prefs (called from the settings toggle). */
  refreshSettings(): void {
    if (!musicEnabled()) {
      this.stop();
      return;
    }
    const a = audioContext();
    if (this.trackGain && a) {
      this.trackGain.gain.cancelScheduledValues(a.currentTime);
      this.trackGain.gain.setTargetAtTime(musicVol(), a.currentTime, 0.05);
    }
    if (!this.timer && this.track) this.start(this.track);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const a = audioContext();
    if (this.trackGain && a) {
      this.trackGain.gain.cancelScheduledValues(a.currentTime);
      this.trackGain.gain.setTargetAtTime(0.0001, a.currentTime, 0.08);
    }
    this.voices = [];
    this.anchored = false;
  }
}

export const Music = new MusicEngine();
